<script lang="ts">
	import { devices_settings } from 'src/stores/device-runtime/status';
	//Formatters
	import { format } from 'svelte-i18n';
	//Helpers
	import { isLastAPConnection } from '../NetworkComponents/network_helpers';
	import { real_id } from '../../../helpers/interface_helpers';
	import { onSettingsResponse } from '../../../helpers/device_settings_helpers';
	//Services
	import { gen2Request } from '../../../services/gen_2_http_reqs';
	//Stores
	import { getDeviceStatus, readDeviceSettings } from 'src/stores/device-runtime/status-selectors';
	//Design system components
	import { Toggle, Checkbox, Button, Alert, Input } from '@Shelly/shelly-design-system';
	//Components
	import NetworkConnectedStatus from '../NetworkComponents/NetworkConnectedStatus.svelte';
	import NetworkModal from '../NetworkComponents/NetworkModal.svelte';
	import StaticIPAddressForm from '../NetworkComponents/StaticIPAddressForm.svelte';
	//Types
	import type { SettingRequest } from '../../../types/requests/settings/setting';
	import type { Device } from '../../../types/Device';
	import { capFirstChar } from '../../../helpers/utilities';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	import { getContext, onDestroy } from 'svelte';
	import type { Writable } from 'svelte/store';
	import type { SidepanelStickyBottomProps } from 'src/components/Sidepanel/sidepanel-types';

	$: statusStore = getDeviceStatus(id);
	export let device: Device;
	export let isOffline;

	const id = real_id(device.id),
		settings = readDeviceSettings(id);

	let enable = settings?.modemmanager?.enable,
		apn = settings?.modemmanager?.apn,
		sim_pin = settings?.modemmanager?.sim_pin,
		modal: NetworkModal,
		waitForResponse = false,
		config: {
			enable: boolean;
			apn: string;
			sim_pin: string | null;
		} = {
			enable,
			apn,
			sim_pin,
		};

	$: connected = config.enable && $statusStore?.modemmanager?.status !== 'disconnected';

	/**Enable
	 * {"method":"modemmanager.setconfig","id":9,"src":"24947511-98c1-4957-9d90-95ca9f0a294c","params":{"config":{"enable":true}}}	1732863309.3570166
	 */

	/**Setup
	 * {"method":"modemmanager.setconfig","id":11,"src":"24947511-98c1-4957-9d90-95ca9f0a294c","params":{"config":{"apn":"vivacom","sim_pin":"0000"}}}	1732863334.9227152
	 */
	const connect = async () => {
			waitForResponse = true;
			const res: SettingRequest = await gen2Request(device, 'modemmanager.SetConfig', {
				config,
			});
			if (res.isok) {
				settings.eth = config;
				devices_settings.update_devices_settings(id, settings);
			}
			onSettingsResponse(res, device);
			waitForResponse = false;
		},
		validate = () => {
			config = {
				enable,
				apn,
				sim_pin,
			};
			if (enable) {
			}
			modal.open(!enable && isLastAPConnection(id));
		};

	const onToggleChange = () => {
		enable = !enable;
		validate();
	};

	const stickyBottomAction =
		getContext<Writable<SidepanelStickyBottomProps | null>>('stickyBottomAction');

	$: if (stickyBottomAction) {
		if ($showNewLayout && enable) {
			stickyBottomAction.set({ onClick: validate, loading: waitForResponse, disabled: isOffline });
		} else {
			stickyBottomAction.set(null);
		}
	}

	onDestroy(() => {
		stickyBottomAction?.set(null);
	});
</script>

{#if $showNewLayout}
	<Toggle
		size="m"
		class="mb-s-plus"
		checked={enable}
		on:click={onToggleChange}
		disabled={isOffline}
		id="checkbox-e8cijmx"
	>
		{capFirstChar($format('sub_settings_title_lte_settings', { default: 'lte settings' }))}
	</Toggle>
	{#if enable}
		<NetworkConnectedStatus {connected} classes="mb-s-plus" />

		<Input
			size="m"
			class="mb-s"
			bind:value={config.apn}
			label="Network provider"
			icon={null}
			id="input-z4g6jx9"
			aria-labelledby="input-z4g6jx9"
		/>
		<Input
			size="m"
			type={'password'}
			bind:value={config.sim_pin}
			label="SIM PIN"
			icon={null}
			id="input-m10kko0"
			aria-labelledby="input-m10kko0"
		/>
	{/if}
{:else}
	<Checkbox size="l" class="mb-s" bind:checked={enable} disabled={isOffline} id="checkbox-e8cijmx">
		{capFirstChar($format('option_enable', { default: 'Enable' }))}
	</Checkbox>

	<hr class="mb-m" />
	<NetworkConnectedStatus {connected} />

	<Input
		size="m"
		class="mb-s"
		bind:value={config.apn}
		label="Network provider"
		icon={null}
		id="input-z4g6jx9"
		aria-labelledby="input-z4g6jx9"
	/>
	<Input
		size="m"
		class="mb-s"
		type={'password'}
		bind:value={config.sim_pin}
		label="SIM PIN"
		icon={null}
		id="input-m10kko0"
		aria-labelledby="input-m10kko0"
	/>

	<Button size="m" width="full" on:click={validate} loading={waitForResponse} disabled={isOffline}>
		{$format('button_save', { default: 'Save' })}
	</Button>
{/if}

<NetworkModal
	bind:this={modal}
	on:confirm={connect}
	{isOffline}
	btnTexts={{
		cancelText: '',
		saveText: enable
			? $format('btn_connect', { default: 'Connect' })
			: $format('label_disconnect', { default: 'Disconnect' }),
	}}
	title={enable
		? $format('connect_to_ethernet_title', {
				default: 'Connect to Ethernet?',
			})
		: $format('disconnect_from_ethernet_title', {
				default: 'Disconnect from Ethernet?',
			})}
>
	{#if enable}
		{$format('connect_to_ethernet_prompt', {
			default: 'Your device will connect to Ethernet. Are you sure you want to proceed?',
		})}
	{:else}
		{$format('disable_ethernet_prompt_message', {
			default: 'Your device will disconnect from Ethernet. Are you sure you want to proceed?',
		})}
	{/if}
</NetworkModal>
